// Feuille de pointage du jour : arrivée, départ, retards, absences.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, Checkbox, IconButton, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ChevronLeft, ChevronRight, AccessTimeOutlined, SaveOutlined, EventBusyOutlined, UploadFileOutlined, DeleteOutline,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import StatutChip from '../../components/rh/StatutChip';
import AbsenceDialog from '../../components/rh/AbsenceDialog';
import ImportPointeuseDialog from '../../components/rh/ImportPointeuseDialog';
import { rhAPI } from '../../services/api';
import { apiError } from '../../utils/format';
import { addDays, duree, periodLabel, todayIso, STATUTS } from '../../utils/rh';
import { tokens } from '../../theme';

const nowHHMM = () => new Date().toTimeString().slice(0, 5);

const Counter = ({ label, value, statut }) => (
  <Box sx={{ px: 2, py: 1.25, borderRadius: 2, bgcolor: STATUTS[statut]?.bg || '#fff', color: STATUTS[statut]?.fg, minWidth: 110 }}>
    <Typography sx={{ fontSize: 12.5, fontWeight: 500 }}>{label}</Typography>
    <Typography sx={{ fontFamily: tokens.mono, fontSize: 22, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

const RhPointage = () => {
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [employes, setEmployes] = useState([]);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [absence, setAbsence] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const isToday = date === todayIso();
  const future = date > todayIso();

  const load = useCallback(() => {
    rhAPI.journee(date).then((r) => { setData(r.data); setEdits({}); }).catch((e) => setError(apiError(e)));
  }, [date]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { rhAPI.employes().then((r) => setEmployes(r.data)).catch(() => {}); }, []);

  const rowValue = (e) => ({ arrivee: e.arrivee || '', depart: e.depart || '', retard_justifie: !!e.retard_justifie, ...edits[e.id] });
  const setRow = (e, changes) => setEdits((prev) => ({ ...prev, [e.id]: { ...rowValue(e), ...changes } }));

  const save = async (e) => {
    const v = rowValue(e);
    try {
      await rhAPI.savePointage({ employe_id: e.id, date, heure_arrivee: v.arrivee, heure_depart: v.depart, retard_justifie: v.retard_justifie });
      setToast(`Pointage de ${e.prenom} ${e.nom} enregistré`);
      load();
    } catch (err) { setError(apiError(err)); }
  };
  const clear = async (e) => {
    try { await rhAPI.deletePointage(e.id, date); setToast('Pointage effacé'); load(); } catch (err) { setError(apiError(err)); }
  };
  const clockIn = (e) => {
    const v = rowValue(e);
    setRow(e, v.arrivee ? { depart: nowHHMM() } : { arrivee: nowHHMM() });
  };

  const counts = useMemo(() => {
    const c = {};
    (data?.employes || []).forEach((e) => { c[e.statut] = (c[e.statut] || 0) + 1; });
    return c;
  }, [data]);

  const actions = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button variant="outlined" startIcon={<UploadFileOutlined />} onClick={() => setImportOpen(true)}>Importer la pointeuse</Button>
      <Button variant="outlined" startIcon={<EventBusyOutlined />} onClick={() => setAbsence({ date_debut: date })}>Déclarer une absence</Button>
    </Box>
  );

  return (
    <Layout title="Pointage du jour" actions={actions} plain>
      {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <IconButton aria-label="Jour précédent" onClick={() => setDate(addDays(date, -1))}><ChevronLeft /></IconButton>
        <TextField type="date" size="small" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} inputProps={{ 'aria-label': 'Date' }} />
        <IconButton aria-label="Jour suivant" onClick={() => setDate(addDays(date, 1))}><ChevronRight /></IconButton>
        {!isToday && <Button size="small" onClick={() => setDate(todayIso())}>Aujourd'hui</Button>}
        <Typography sx={{ ml: 1, fontWeight: 600, textTransform: 'capitalize' }}>{periodLabel('jour', date)}</Typography>
      </Box>

      {data?.ferie && <Alert severity="info" sx={{ mb: 2 }}>Jour férié : {data.ferie}</Alert>}
      {future && <Alert severity="info" sx={{ mb: 2 }}>Date future : le pointage n'est pas encore possible.</Alert>}

      {data && (
        <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
          <Counter label="Présents" value={(counts.PRESENT || 0) + (counts.RETARD || 0)} statut="PRESENT" />
          <Counter label="Dont en retard" value={counts.RETARD || 0} statut="RETARD" />
          <Counter label="Absents" value={(counts.ABSENT_J || 0) + (counts.ABSENT_NJ || 0)} statut="ABSENT_NJ" />
          <Counter label="En congé" value={counts.CONGE || 0} statut="CONGE" />
          <Counter label={isToday ? 'Pas encore pointés' : 'Non pointés'} value={(counts.NON_POINTE || 0) + (counts.EN_ATTENTE || 0)} statut="NON_POINTE" />
        </Box>
      )}

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Employé</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell>Arrivée</TableCell>
                <TableCell>Départ</TableCell>
                <TableCell>Retard</TableCell>
                <TableCell>Justifié</TableCell>
                <TableCell align="right">Travail</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {(data?.employes || []).map((e) => {
                const v = rowValue(e);
                const dirty = !!edits[e.id];
                const locked = future || ['CONGE', 'ABSENT_J', 'ABSENT_NJ', 'HORS_CONTRAT'].includes(e.statut);
                return (
                  <TableRow key={e.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: 14 }}>{e.nom} {e.prenom}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{e.matricule}{e.poste ? ` · ${e.poste}` : ''}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatutChip statut={e.statut} />
                      {e.detail && <Typography sx={{ fontSize: 11.5, color: 'text.secondary', mt: 0.25 }}>{e.detail.toLowerCase().replace('_', ' ')}</Typography>}
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <TextField type="time" size="small" value={v.arrivee} disabled={locked}
                        inputProps={{ 'aria-label': `Arrivée ${e.prenom} ${e.nom}` }} onChange={(ev) => setRow(e, { arrivee: ev.target.value })} />
                    </TableCell>
                    <TableCell sx={{ width: 130 }}>
                      <TextField type="time" size="small" value={v.depart} disabled={locked}
                        inputProps={{ 'aria-label': `Départ ${e.prenom} ${e.nom}` }} onChange={(ev) => setRow(e, { depart: ev.target.value })} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: tokens.mono, color: e.retard_min ? 'warning.main' : 'text.secondary', whiteSpace: 'nowrap' }}>
                      {e.retard_min ? duree(e.retard_min) : '—'}
                    </TableCell>
                    <TableCell padding="checkbox">
                      {(e.retard_min > 0 || v.retard_justifie) && (
                        <Checkbox size="small" checked={v.retard_justifie} inputProps={{ 'aria-label': `Retard justifié ${e.nom}` }}
                          onChange={(ev) => setRow(e, { retard_justifie: ev.target.checked })} />
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{e.travail_min ? duree(e.travail_min) : ''}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {isToday && !locked && (
                        <Tooltip title={v.arrivee ? 'Départ maintenant' : 'Arrivée maintenant'}>
                          <IconButton size="small" aria-label={`Pointer maintenant ${e.nom}`} onClick={() => clockIn(e)}><AccessTimeOutlined fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {!locked && (
                        <Tooltip title="Enregistrer">
                          <span>
                            <IconButton size="small" color="primary" disabled={!dirty} aria-label={`Enregistrer ${e.nom}`} onClick={() => save(e)}>
                              <SaveOutlined fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {(e.arrivee || e.depart) && (
                        <Tooltip title="Effacer le pointage">
                          <IconButton size="small" aria-label={`Effacer ${e.nom}`} onClick={() => clear(e)}><DeleteOutline fontSize="small" /></IconButton>
                        </Tooltip>
                      )}
                      {!locked && !e.arrivee && (
                        <Tooltip title="Déclarer absent">
                          <IconButton size="small" aria-label={`Absent ${e.nom}`} onClick={() => setAbsence({ employe_id: e.id, date_debut: date, motif: 'INJUSTIFIEE', justifiee: false })}>
                            <EventBusyOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {data && data.employes.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>Aucun employé. Ajoutez le personnel dans « Employés ».</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      {data && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Horaire : {data.params.heure_debut} – {data.params.heure_fin}, tolérance {data.params.tolerance_minutes} min.
          Un retard au-delà de la tolérance est compté depuis l'heure de début.
        </Typography>
      )}

      <AbsenceDialog open={!!absence} value={absence} employes={employes} onClose={() => setAbsence(null)}
        onSaved={(m) => { setAbsence(null); setToast(m); load(); }} />
      <ImportPointeuseDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default RhPointage;
